import { useSeoMeta, useHead } from '@unhead/react';
import {CheckCircle2, Truck, Package, Briefcase, Shield} from 'lucide-react';
import { Link } from 'react-router-dom';
import { SiteShell } from '@/components/SiteShell';
import { PageHero } from '@/components/PageHero';
import { normalizeCanonicalUrl } from '@/lib/seo';
import { ServiceCategoryLinks } from '@/components/SiteLinks';
import servicesBg from '../../images/VIP House Movers Removal Van.jpg';
import serviceImg1 from '../../images/VIP House Movers Team Carrying Items.jpg';
import serviceImg2 from '../../images/VIP House Movers Team Packing.jpg';
import serviceImg3 from '../../images/VIP House Movers Removal Team working.jpg';
import serviceImg4 from '../../images/VIP House Movers Removal Boxes.jpg';

export default function Services() {
  useSeoMeta({
    title: 'Removal Services Wolverhampton | House Removals, Man and Van & Packing | VIP House Movers',
    description: 'Professional house removals, man and van, packing, office clearances & storage in Wolverhampton & West Midlands. Eco-friendly, fully insured removals. Free quote: 0121 752 7107.',
    ogTitle: 'Removal Services Wolverhampton | VIP House Movers',
    ogDescription: 'Professional house removals, packing, office clearances & storage across the West Midlands. Eco-friendly & fully insured.',
    ogImage: 'https://www.viphousemovers.com/og-home.jpg',
    ogUrl: 'https://www.viphousemovers.com/services',
    ogType: 'website',
    twitterCard: 'summary_large_image',
    twitterTitle: 'Removal Services | VIP House Movers Wolverhampton',
    twitterDescription: 'Professional removals, packing & storage across the West Midlands.',
    twitterImage: 'https://www.viphousemovers.com/og-home.jpg',
  });
  const canonicalHref = normalizeCanonicalUrl('https://www.viphousemovers.com/services');

  useHead({
    link: [{ rel: 'canonical', href: canonicalHref }],
    meta: [
      {
        name: 'keywords',
        content: 'removal services Wolverhampton, house removals West Midlands, man and van service, packing services, office removals, storage solutions, local removal company, professional movers Wolverhampton, insured removals, eco-friendly movers',
      },
    ],
    script: [
      {
        type: 'application/ld+json',
        children: JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          itemListElement: [
            {
              '@type': 'ListItem',
              position: 1,
              name: 'Home',
              item: 'https://www.viphousemovers.com',
            },
            {
              '@type': 'ListItem',
              position: 2,
              name: 'Services',
              item: 'https://www.viphousemovers.com/services',
            },
          ],
        }),
      },
      {
        type: 'application/ld+json',
        children: JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'Service',
          name: 'Removal Services',
          provider: {
            '@type': 'LocalBusiness',
            name: 'VIP House Movers',
            url: 'https://www.viphousemovers.com',
            telephone: '+44-121-752-7107',
            address: {
              '@type': 'PostalAddress',
              addressLocality: 'Wolverhampton',
              addressRegion: 'West Midlands',
              addressCountry: 'GB',
            },
          },
          areaServed: [
            'Wolverhampton',
            'Birmingham',
            'Walsall',
            'Dudley',
            'Staffordshire',
            'Cannock',
            'Telford',
            'Lichfield',
            'Tamworth',
            'Sutton Coldfield',
            'West Bromwich',
            'Kidderminster',
            'Stourbridge',
          ],
          serviceType: [
            'House Removals',
            'Office Removals',
            'Packing Services',
            'Storage Solutions',
            'Man and Van',
            'Furniture Assembly',
            'Piano Removals',
          ],
        }),
      },
    ],
  });
  return (
    <SiteShell activePath="/services" fixedNav>
      <PageHero
        eyebrow="VIP House Movers Services"
        title="Our Services"
        description="Tailored moving solutions designed for minimal disruption and maximum peace of mind across homes, offices, and specialist relocations."
        align="center"
        backgroundImage={servicesBg}
      />

      {/* Service Categories Navigation - Improves SEO */}
      <section className="bg-white py-12 border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Browse Our Services</h2>
          <ServiceCategoryLinks />
        </div>
      </section>

      {/* Detailed Services Section */}
      <section className="py-24 bg-gray-50 relative overflow-hidden border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-12">
            
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 hover:border-[#D4AF37]/30 transition-colors overflow-hidden">
              <img
                src={serviceImg1}
                alt="VIP House Movers team completing a professional home removal in Wolverhampton, West Midlands"
                width={1000}
                height={600}
                loading="lazy"
                decoding="async"
                className="h-56 w-full object-cover"
              />
              <div className="p-8">
              <div className="w-16 h-16 bg-[#D4AF37]/10 rounded-full flex items-center justify-center mb-6">
                <Truck className="w-8 h-8 text-[#D4AF37]" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Home Removals</h3>
              <p className="text-gray-600 font-light leading-relaxed mb-6">
                Comprehensive domestic moving services. From single items to full large family house relocations, we handle every move with care and precision, anywhere in the UK.
              </p>
               <ul className="space-y-3">
                  <li className="flex items-center gap-3"><CheckCircle2 className="w-5 h-5 text-[#D4AF37]" /><span className="text-gray-700">Fully insured transport</span></li>
                  <li className="flex items-center gap-3"><CheckCircle2 className="w-5 h-5 text-[#D4AF37]" /><span className="text-gray-700">Dismantling & reassembly</span></li>
                  <li className="flex items-center gap-3"><CheckCircle2 className="w-5 h-5 text-[#D4AF37]" /><span className="text-gray-700">Floor and wall protection</span></li>
               </ul>
              </div>
            </div>

            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 hover:border-[#D4AF37]/30 transition-colors overflow-hidden">
              <img
                src={serviceImg2}
                alt="VIP House Movers professional packing service wrapping fragile items in eco-friendly materials"
                width={1000}
                height={600}
                loading="lazy"
                decoding="async"
                className="h-56 w-full object-cover"
              />
              <div className="p-8">
              <div className="w-16 h-16 bg-[#D4AF37]/10 rounded-full flex items-center justify-center mb-6">
                <Package className="w-8 h-8 text-[#D4AF37]" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Packing Services</h3>
              <p className="text-gray-600 font-light leading-relaxed mb-6">
                Professional packing using high-quality materials to ensure your fragile and valuable items are perfectly protected during transit. We offer full or partial packing.
              </p>
                <ul className="space-y-3">
                  <li className="flex items-center gap-3"><CheckCircle2 className="w-5 h-5 text-[#D4AF37]" /><span className="text-gray-700">Eco-friendly materials</span></li>
                  <li className="flex items-center gap-3"><CheckCircle2 className="w-5 h-5 text-[#D4AF37]" /><span className="text-gray-700">Specialist care for fragile items</span></li>
                  <li className="flex items-center gap-3"><CheckCircle2 className="w-5 h-5 text-[#D4AF37]" /><span className="text-gray-700">Unpacking services available</span></li>
               </ul>
              </div>
            </div>

            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 hover:border-[#D4AF37]/30 transition-colors overflow-hidden">
              <img
                src={serviceImg3}
                alt="VIP House Movers efficient office clearance and commercial relocation in the West Midlands"
                width={1000}
                height={600}
                loading="lazy"
                decoding="async"
                className="h-56 w-full object-cover"
              />
              <div className="p-8">
              <div className="w-16 h-16 bg-[#D4AF37]/10 rounded-full flex items-center justify-center mb-6">
                <Briefcase className="w-8 h-8 text-[#D4AF37]" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Office Clearances</h3>
              <p className="text-gray-600 font-light leading-relaxed mb-6">
                Efficient commercial moves and clearances tailored to minimize downtime. We handle IT equipment, furniture, and sensitive documents securely.
              </p>
               <ul className="space-y-3">
                  <li className="flex items-center gap-3"><CheckCircle2 className="w-5 h-5 text-[#D4AF37]" /><span className="text-gray-700">Out-of-hours service</span></li>
                  <li className="flex items-center gap-3"><CheckCircle2 className="w-5 h-5 text-[#D4AF37]" /><span className="text-gray-700">Secure document disposal</span></li>
                  <li className="flex items-center gap-3"><CheckCircle2 className="w-5 h-5 text-[#D4AF37]" /><span className="text-gray-700">Minimal business disruption</span></li>
               </ul>
              </div>
            </div>

            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 hover:border-[#D4AF37]/30 transition-colors overflow-hidden">
              <img
                src={serviceImg4}
                alt="VIP House Movers secure clean storage facility in Wolverhampton for household and business items"
                width={1000}
                height={600}
                loading="lazy"
                decoding="async"
                className="h-56 w-full object-cover"
              />
              <div className="p-8">
              <div className="w-16 h-16 bg-[#D4AF37]/10 rounded-full flex items-center justify-center mb-6">
                <Shield className="w-8 h-8 text-[#D4AF37]" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Storage Solutions</h3>
              <p className="text-gray-600 font-light leading-relaxed mb-6">
                Secure, dry, and clean long or short-term storage facilities available for when you need a little extra space or time during your transition.
              </p>
               <ul className="space-y-3">
                  <li className="flex items-center gap-3"><CheckCircle2 className="w-5 h-5 text-[#D4AF37]" /><span className="text-gray-700">24/7 CCTV Monitoring</span></li>
                  <li className="flex items-center gap-3"><CheckCircle2 className="w-5 h-5 text-[#D4AF37]" /><span className="text-gray-700">Climate-controlled options</span></li>
                  <li className="flex items-center gap-3"><CheckCircle2 className="w-5 h-5 text-[#D4AF37]" /><span className="text-gray-700">Flexible term lengths</span></li>
               </ul>
              </div>
            </div>

          </div>
        </div>
      </section>

      <section className="bg-white py-20 border-t border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid gap-10 lg:grid-cols-[1.05fr_0.95fr] items-start">
            <div>
              <p className="text-sm font-semibold uppercase tracking-[0.2em] text-[#0F4A4E]">Home Removals SEO Content</p>
              <h2 className="mt-4 text-4xl font-bold tracking-tight text-gray-900">What customers expect from a top-rated home removals company</h2>
              <p className="mt-5 text-lg leading-relaxed text-gray-600">
                VIP House Movers is structured to answer the questions real customers search for before booking a move: how much home removals cost, what is included, whether the company is insured, and how to reduce stress on moving day.
              </p>
              <div className="mt-8 grid gap-4 md:grid-cols-2">
                {[
                  'House removals for flats, terraces, detached homes, and larger family properties.',
                  'Flexible support for packing, furniture dismantling, unloading, and room placement.',
                  'Coverage across Wolverhampton, Birmingham, Walsall, Dudley, Staffordshire, and nationwide UK routes.',
                  'Eco-friendly moving options with recyclable materials and route-conscious planning.',
                ].map((item) => (
                  <div key={item} className="rounded-2xl border border-gray-200 bg-gray-50 p-5 text-gray-700 shadow-sm">
                    {item}
                  </div>
                ))}
              </div>
            </div>
            <div className="rounded-3xl border border-gray-200 bg-gray-50 p-8 shadow-sm">
              <h3 className="text-2xl font-bold text-gray-900">Popular service searches we cover</h3>
              <div className="mt-6 flex flex-wrap gap-3">
                {[
                  'house removals Wolverhampton',
                  'home removals West Midlands',
                  'packing services Wolverhampton',
                  'man and van Wolverhampton',
                  'office removals West Midlands',
                  'long distance removals UK',
                  'flat removals Birmingham',
                  'storage solutions Wolverhampton',
                ].map((term) => (
                  <span key={term} className="rounded-full border border-[#0F4A4E]/20 bg-white px-4 py-2 text-sm font-semibold text-[#0F4A4E] shadow-sm">
                    {term}
                  </span>
                ))}
              </div>
              <p className="mt-6 leading-relaxed text-gray-600">
                This richer service content helps search engines understand the full breadth of VIP House Movers while giving customers clearer reasons to enquire.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-gray-900 text-white py-20 relative border-t-8 border-[#D4AF37]">
         <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
           <h2 className="text-4xl font-bold mb-6 tracking-tight">Need a service not listed here?</h2>
           <p className="text-xl text-gray-300 mb-10 max-w-2xl mx-auto font-light">
             We handle all types of bespoke moving requirements. Get in touch to discuss your specific needs.
           </p>
            <Link to="/quote" className="inline-flex justify-center items-center gap-3 bg-[#D4AF37] text-gray-900 px-8 py-4 rounded-xl font-bold text-lg hover:bg-white hover:scale-105 transition-all duration-300 shadow-lg shadow-[#D4AF37]/20">
              Request a Custom Quote
            </Link>
         </div>
       </section>
    </SiteShell>
  );
}

